import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminPriceList } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Chip } from "@/ui/Chip";
import { Button } from "@/ui/Button";
import { PlatformActions } from "@/features/platform/screens";
import { Dialog } from "@/ui/Overlay";
import { Field, Input, Textarea } from "@/ui/Field";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/prices")({
  component: PricesScreen,
});

/**
 * The hourly rates, which are no longer what anybody is charged.
 *
 * # WHAT THIS SCREEN IS FOR NOW
 *
 * Capacity is rented by the month, so these rates price nothing. They are kept
 * for two reasons and both are worth stating, because a screen full of prices
 * nobody pays is otherwise a trap.
 *
 * The invoices drawn before the change were computed from the list in force
 * when each hour was metered, and a customer querying one a year later has to
 * be able to see the inputs. And the plan prices were derived from these: each
 * plan costs what a month of holding every one of its ceilings came to at these
 * rates, which is the only reason the figures are the odd numbers they are.
 *
 * # WHY PUBLISHING AND NEVER EDITING
 *
 * A metered hour is priced by the list in force when it was metered, and the
 * usage row records which list that was. So a price change tomorrow cannot
 * restate what last week cost, and an invoice a customer queries a year later
 * can be recomputed from the same inputs. Editing a price in place would make
 * every past hour unreproducible in one UPDATE, which is why there is no form
 * here that does it.
 *
 * Until this screen existed, prices could only change by deploying code.
 */
function PricesScreen() {
  const [publishing, setPublishing] = useState(false);

  const prices = useQuery({
    queryKey: ["platform-prices"],
    queryFn: ({ signal }) =>
      api.get<{ prices: AdminPriceList[] | null }>("/admin/prices", undefined, signal),
  });

  const list = prices.data?.prices ?? [];
  const current = list.find((p) => p.in_force);

  return (
    <div className="flex flex-col gap-4">
      {/* Beside the title rather than in a band of its own. It was the only
          thing in that band: one button against the right edge with a
          thousand empty pixels next to it. */}
      <PlatformActions>
        <Button tone="primary" size="dense" onClick={() => setPublishing(true)}>
          Publish a new list
        </Button>
      </PlatformActions>

      <Panel
        description="These rates priced the invoices drawn before capacity was rented, and the plan prices were derived from them. Nothing is charged by the hour any more: a plan is one price a month, and the readings an account can see carry no money."
        padded={false}
      >
        {prices.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={3} columns={5} />
          </div>
        ) : prices.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={prices.error} onRetry={() => void prices.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState
              title="No price list"
              description="Nothing can be measured until one exists. Measurement is all these rates do now."
            />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[22%]">From</Th>
                  <Th width="w-[19%]" numeric>
                    CPU / core
                  </Th>
                  <Th width="w-[19%]" numeric>
                    Memory / GiB
                  </Th>
                  <Th width="w-[19%]" numeric>
                    Storage / GiB
                  </Th>
                  <Th width="w-[21%]">Note</Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <Tr key={p.id} className={p.in_force ? "bg-[var(--primary-soft)]" : undefined}>
                    <Td clip title={absoluteTime(p.effective_from)}>
                      {new Date(p.effective_from).toLocaleDateString()}
                      {p.in_force && (
                        <Chip tone="live" className="ml-2">
                          in force
                        </Chip>
                      )}
                      {new Date(p.effective_from).getTime() > Date.now() && (
                        <Chip tone="warn" className="ml-2">
                          scheduled
                        </Chip>
                      )}
                    </Td>
                    {/* Per month, because that is the only form anybody can
                        reason about. The hourly figure is on hover: a price
                        per core-hour tells nobody anything. */}
                    <Td numeric title={`${p.cpu_per_core_hour} per core-hour`}>
                      {p.cpu_per_core_month}
                    </Td>
                    <Td numeric title={`${p.memory_per_gib_hour} per GiB-hour`}>
                      {p.memory_per_gib_month}
                    </Td>
                    <Td numeric title={`${p.storage_per_gib_hour} per GiB-hour`}>
                      {p.storage_per_gib_month}
                    </Td>
                    <Td clip title={p.note || undefined}>
                      <span className="text-[var(--text-secondary)]">{p.note || "—"}</span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      {publishing && (
        <PublishDialog
          current={current}
          onClose={() => setPublishing(false)}
          onPublished={async () => {
            setPublishing(false);
            await prices.refetch();
          }}
        />
      )}
    </div>
  );
}

/** 730 hours: the average month, so the figure does not move between February and March. */
const HOURS_PER_MONTH = 730;

function PublishDialog({
  current,
  onClose,
  onPublished,
}: {
  current?: AdminPriceList;
  onClose: () => void;
  onPublished: () => void | Promise<unknown>;
}) {
  const toast = useToast();
  // Prefilled from what is in force, so a change is a change rather than a
  // fresh set of numbers somebody has to look up first.
  const [cpu, setCPU] = useState(() => micros(current?.cpu_per_core_hour));
  const [memory, setMemory] = useState(() => micros(current?.memory_per_gib_hour));
  const [storage, setStorage] = useState(() => micros(current?.storage_per_gib_hour));
  const [from, setFrom] = useState(() => tomorrow());
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");

  const publish = useMutation({
    mutationFn: () =>
      api.post<unknown>("/admin/prices", {
        effective_from: new Date(from).toISOString(),
        cpu_micros_per_core_hour: Number(cpu),
        memory_micros_per_gib_hour: Number(memory),
        storage_micros_per_gib_hour: Number(storage),
        note: note.trim() || undefined,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      toast.success("The price list is published");
      await onPublished();
    },
    onError: toast.error,
  });

  const ready =
    reason.trim().length >= 3 &&
    from !== "" &&
    new Date(from).getTime() > Date.now() &&
    [cpu, memory, storage].every((v) => v !== "" && Number.isFinite(Number(v)));

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Publish a price list"
      description="A new version, from a date in the future. Hours already metered keep the prices they were metered at."
      size="lg"
      footer={
        <>
          <Button tone="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button tone="primary" disabled={!ready} busy={publish.isPending} onClick={() => publish.mutate()}>
            Publish
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="In force from"
          hint="Has to be in the future. A list starting now or earlier would reprice hours that are already on invoices."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          )}
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Rate label="CPU, per core-hour" value={cpu} onChange={setCPU} />
          <Rate label="Memory, per GiB-hour" value={memory} onChange={setMemory} />
          <Rate label="Storage, per GiB-hour" value={storage} onChange={setStorage} />
        </div>

        {/* What the numbers mean in the only unit anybody reasons in. Micros
            per hour is the unit the meter works in and the unit nobody can
            picture; a customer asks what a core costs per month. */}
        <div className="rounded-lg border border-[var(--border)] bg-[var(--surface-2)] px-4 py-3">
          <p className="text-[13px] text-[var(--text-secondary)]">
            That comes to {euro(cpu)} per core, {euro(memory)} per GiB of memory and{" "}
            {euro(storage)} per GiB of storage, each per month.
          </p>
        </div>

        <Field label="Note" hint="Shown on the list. Why these numbers, for whoever reads it next.">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={note}
              placeholder="Hardware refresh, October 2026"
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>

        <Field label="Reason" hint="Recorded in the audit log.">
          {({ id, describedBy }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

function Rate({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <Field label={label} hint="In micro-euro">
      {({ id, describedBy }) => (
        <Input
          id={id}
          aria-describedby={describedBy}
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))}
        />
      )}
    </Field>
  );
}

/** Micro-euro back out of a rendered rate, for prefilling the form. */
function micros(rate: string | undefined): string {
  if (!rate) return "";
  const n = Number(rate.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n * 1_000_000));
}

function euro(microsPerHour: string): string {
  const n = Number(microsPerHour);
  if (!Number.isFinite(n)) return "—";
  return `${((n * HOURS_PER_MONTH) / 1_000_000).toFixed(2)} EUR`;
}

/** Tomorrow at this time, in the shape a datetime-local input wants. */
function tomorrow(): string {
  const t = new Date(Date.now() + 86400000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${t.getFullYear()}-${pad(t.getMonth() + 1)}-${pad(t.getDate())}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
}
